/**
 * AirportLabs Analytics SDK wrapper. Loads the platform SDK from its `/sdk`
 * endpoint and emits page views alongside the existing `POST /api/analytics`
 * flow. No-ops when unconfigured. Tag keys must be snake_case; tag values must
 * be `{ value, description }`.
 */

const SCRIPT_ID = "al-analytics-js";

let bootstrapped = false;
let enabled = false;
// Whether a page_view is currently "open" (awaiting its page_leave). Tracked at
// module scope so it spans the many useTrackPageAnalytics instances.
let pageOpen = false;

export function toTag(value, description) {
  return { value: value ?? null, description };
}

// Runs `action` only when the SDK is enabled, swallowing errors so analytics
// can never break the app.
function safe(action) {
  if (!enabled) return;
  try {
    action();
  } catch (error) {
    console.error("Analytics error:", error);
  }
}

// Stub `window.al` that buffers commands; the real SDK replays the queued
// dataLayer (an array of [command, ...args]) in order once it loads.
function installFallbackStub() {
  window.al = window.al || {};
  window.al.dataLayer = window.al.dataLayer || [];
  window.al.trackEvent =
    window.al.trackEvent || ((...args) => window.al.dataLayer.push(args));
  window.al.trackPageView =
    window.al.trackPageView ||
    ((args = {}) => window.al.dataLayer.push(["event", "page_view", args]));
}

// Installs the stub, sets app config + default tags, and appends the SDK
// script. Idempotent; no-ops when scriptUrl/appHash are missing.
export function bootstrapAnalytics({
  realm,
  deviceId,
  appHash,
  scriptUrl,
} = {}) {
  if (bootstrapped) return;
  bootstrapped = true;
  if (!scriptUrl || !appHash) return;

  try {
    installFallbackStub();

    window.al.trackEvent("config", "PocketFlights", { preventDefault: true });
    window.al.trackEvent("set", {
      realm: toTag(realm, "The realm/airport code"),
      device_id: toTag(deviceId, "PF unique device identifier"),
      app_hash: toTag(appHash, "The app hash"),
    });

    if (!document.getElementById(SCRIPT_ID)) {
      const script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.src = scriptUrl;
      script.async = true;
      document.head.appendChild(script);
    }

    enabled = true;

    // The SDK's own unload listeners are off (preventDefault), so flush the
    // final page's dwell ourselves when the app is hidden/closed.
    window.addEventListener("pagehide", flushPageLeave);
  } catch (error) {
    console.error("Could not bootstrap analytics SDK:", error);
  }
}

export function areAnalyticsConfigured() {
  return enabled;
}

// Only page_view source: the SDK's auto-tracking is off (preventDefault). Each
// new page view first closes the previous page with a page_leave carrying
// time_on_page (seconds since that view), so "Avg time on page" has a source.
export function trackPageView(tags = {}) {
  safe(() => {
    if (pageOpen) window.al.trackPageLeave();
    window.al.trackPageView(tags);
    pageOpen = true;
  });
}

// Emits a page_leave (time_on_page) for the currently-open page, if any. Used on
// pagehide so the last page before close/backgrounding is also recorded.
export function flushPageLeave() {
  safe(() => {
    if (pageOpen) {
      window.al.trackPageLeave();
      pageOpen = false;
    }
  });
}

// Custom (non-page_view) SDK event, mirroring the PF backend `type:"event"`
// hits. `eventType` must be snake_case; tag values must be `{ value, description }`.
// `realm`, `device_id` and `app_hash` are attached automatically as default tags.
export function trackEvent(eventType, tags = {}) {
  safe(() => window.al.trackEvent("event", eventType, tags));
}
